import React from 'react';
import {
  LayoutDashboard, LayoutGrid, Map as MapIcon, ShieldCheck,
  Zap, BookOpen, Settings, HelpCircle,
} from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { useProductTour } from '../../context/ProductTourContext';
import { TOUR_SECTIONS } from '../../constants/productTour';

const ICONS = {
  dashboard: LayoutDashboard,
  overview: LayoutGrid,
  map: MapIcon,
  disclosure: ShieldCheck,
  actions: Zap,
  reports: BookOpen,
  setup: Settings,
  glossary: HelpCircle,
};

export default function TourHubModal() {
  const {
    phase,
    afterSection,
    farmId,
    startSection,
    finishTour,
    skipTour,
    status,
  } = useProductTour();

  const isReplay = status === 'completed' || status === 'skipped';
  const onHubClose = afterSection ? finishTour : skipTour;

  return (
    <Modal
      isOpen={phase === 'hub'}
      onClose={onHubClose}
      title={afterSection ? 'Explore another section?' : 'Where would you like to start?'}
      maxWidthClass="max-w-2xl"
    >
      <p className="text-sm text-slate-600 mb-4">
        {afterSection
          ? 'Pick another area, or finish and look around on your own. You can reopen this from Help at any time.'
          : 'Choose a section. We will take you there and highlight the useful parts of that view.'}
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
        {TOUR_SECTIONS.map((section) => {
          const Icon = ICONS[section.id] || LayoutGrid;
          const needsFarm = section.needsProperty && !farmId;
          return (
            <button
              key={section.id}
              type="button"
              disabled={needsFarm}
              title={needsFarm ? 'Open a property to view this' : undefined}
              onClick={() => { if (!needsFarm) startSection(section.id); }}
              className={`text-left rounded-xl border px-3.5 py-3 transition-colors ${
                needsFarm
                  ? 'border-slate-100 bg-slate-50 text-slate-300 cursor-not-allowed'
                  : 'border-slate-200 bg-white hover:border-eddp-teal/40 hover:bg-eddp-cream2/60'
              }`}
            >
              <span className="flex items-center gap-2 mb-1">
                <Icon size={16} className={needsFarm ? 'text-slate-300' : 'text-eddp-teal'} />
                <span className={`text-sm font-semibold ${needsFarm ? 'text-slate-400' : 'text-eddp-dark'}`}>
                  {section.label}
                </span>
              </span>
              <span className={`block text-[12px] leading-snug ${needsFarm ? 'text-slate-300' : 'text-slate-500'}`}>
                {needsFarm ? 'Open a property to view this' : section.blurb}
              </span>
            </button>
          );
        })}
      </div>
      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end mt-5">
        {!isReplay && (
          <Button type="button" variant="ghost" onClick={skipTour}>
            Skip tour
          </Button>
        )}
        <Button type="button" variant={afterSection ? 'primary' : 'secondary'} onClick={onHubClose}>
          {afterSection || isReplay ? 'Finish' : 'Get started'}
        </Button>
      </div>
    </Modal>
  );
}
